// File: UomConversionMatrix.js

import React, { useCallback, useEffect, useState, useRef } from "react";
import Loader from "../../../loader/Loader"; // Adjust path as needed
import { Search } from "lucide-react";
import { toast } from "react-toastify";
import ReactDOM from "react-dom";
import axios from "axios";
import { domain, domainAccounting } from "../../../../security"; // Adjust path as needed

const SearchableSelect = ({
  options,
  value,
  onChange,
  placeholder,
  disabled,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [dropdownStyle, setDropdownStyle] = useState({});
  const wrapperRef = useRef(null);
  const triggerRef = useRef(null);
  const dropdownRef = useRef(null); // ✅ NEW: ref for the portal dropdown

  const selectedOption = options.find((o) => {
    return o?.id?.toString() === value?.toString()
  });

  const openDropdown = () => {
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setDropdownStyle({
        position: "fixed",
        top: rect.bottom + 4,
        left: rect.left,
        width: rect.width,
        zIndex: 9999,
      });
    }
    setIsOpen(true);
    setSearch("");
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      // ✅ Also ignore clicks inside the portal dropdown
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(event.target) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const filteredOptions = options.filter((o) =>
    o.label?.toLowerCase().includes(search?.toLowerCase()),
  );

  return (
    <div className="relative w-full" ref={wrapperRef}>
      <div
        ref={triggerRef}
        className={`w-full border border-slate-300 rounded-xl px-3 py-2 text-sm bg-white flex justify-between items-center ${
          disabled
            ? "opacity-60 cursor-not-allowed bg-slate-50"
            : "cursor-pointer"
        }`}
        onClick={() => {
          if (!disabled) isOpen ? setIsOpen(false) : openDropdown();
        }}
      >
        <span
          className={`truncate pr-2 ${selectedOption ? "text-slate-800 font-medium" : "text-slate-400"}`}
        >
          {selectedOption ? selectedOption.productName : placeholder}
        </span>
        <Search className="h-4 w-4 text-slate-400 flex-shrink-0" />
      </div>

      {isOpen &&
        ReactDOM.createPortal(
          <div
            ref={dropdownRef} // ✅ Attach ref here so clicks inside are recognized
            style={dropdownStyle}
            className="bg-white border border-slate-200 rounded-xl shadow-xl flex flex-col overflow-hidden"
          >
            <div className="bg-slate-50 p-2 border-b border-slate-100">
              <input
                type="text"
                className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                placeholder="Type to search..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onClick={(e) => e.stopPropagation()}
                autoFocus
              />
            </div>
            <div className="max-h-56 overflow-y-auto">
              {filteredOptions.length > 0 ? (
                filteredOptions.map((o) => (
                  <div
                    key={o.id}
                    className="px-3 py-2.5 text-sm hover:bg-purple-50 cursor-pointer text-slate-700 border-b border-slate-50 last:border-0"
                    onClick={() => {
                      onChange(o.id);
                      setIsOpen(false);
                    }}
                  >
                    {`${o.productName} ${o.brandName ? `(${o.brandName})` : ""}`}
                  </div>
                ))
              ) : (
                <div className="px-3 py-4 text-sm text-center text-slate-400">
                  No results found
                </div>
              )}
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
};

const AddUomConversion = ({ onClose, refreshData, conversionToEdit }) => {
  // State to hold all available Units of Measurement
  const [uoms, setUoms] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [productId, setProductId] = useState(null)
  const [itemList, setItemList] = useState([])

  const fetchItemListData = useCallback(async () => {
    setIsLoading(true);

    try {
      const response = await axios.get(`${domain}/api/Products`, {
        headers: { 'Content-Type': 'application/json'}
      })
        
      setItemList(response.data)
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchItemListData()
  }, [fetchItemListData])

  // State to hold the dynamic list of conversion rules
  const [conversions, setConversions] = useState([]);

  useEffect(() => {
    const fetchUoms = async () => {
      setIsLoading(true);
      try {
        const uomsUrl = `${domain}/api/UnitOfMeasurements`;
        const response = await axios.get(uomsUrl);
        setUoms(response.data);
        // Start with one empty row for the user
        handleAddConversionRow();
      } catch (error) {
        console.error("Failed to fetch UOM data:", error);
        toast.error("Could not load units of measurement.");
      } finally {
        setIsLoading(false);
      }
    };

    fetchUoms();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if(conversionToEdit){
      setConversions([conversionToEdit])
      setProductId(conversionToEdit?.productId ?? null)
    }
  }, [conversionToEdit])

  // Handler to add a new, empty conversion row
  const handleAddConversionRow = () => {
    if(!conversionToEdit){
      setConversions([
        ...conversions,
        // Use a unique ID for React's key prop and for easy removal
        {
          id: Date.now() + Math.random(), // Simple unique ID
          productId: "",
          fromUomId: "",
          toUomId: "",
          conversionRate: "",
        },
      ]);
    }
  };

  // Handler to remove a conversion row by its unique ID
  const handleRemoveConversionRow = (id) => {
    setConversions(conversions.filter((conv) => conv.id !== id));
  };

  // Handler to update a specific field in a specific conversion row
  const handleConversionChange = (id, field, value) => {
    setConversions(
      conversions.map((conv) =>
        conv.id === id ? { ...conv, [field]: value } : conv
      )
    );
  };

  const getItemCode = useCallback(async (_productId) => {
    try {
      const response = await axios.get(`${domain}/api/Products/get-item-code/${_productId}`, {
        headers: { 'Content-Type': 'application/json'}
      })
        
      return (response.data)
    } catch (error) {
      console.error('Error fetching data:', error);
      return null
    }
  }, []);

  // Handler for form submission
  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    const validConversions = conversions
      .filter(
        (c) =>
          c.productId &&
          c.fromUomId &&
          c.toUomId &&
          c.conversionRate &&
          !isNaN(parseFloat(c.conversionRate))
      )
      .map((c) => ({
        productId: parseInt(c.productId),
        fromUomId: parseInt(c.fromUomId, 10),
        fromUomCode: c.fromUomCode,
        toUomId: parseInt(c.toUomId, 10),
        toUomCode: c.toUomCode,
        conversionRate: parseFloat(c.conversionRate),
      }));

    // Check for duplicate fromUomId -> toUomId pairs
    const uniqueKeys = new Set();
    const payload = [];
    for (const conv of validConversions) {
      const key = `${conv.fromUomId}-${conv.toUomId}`;
      if (!uniqueKeys.has(key)) {
        uniqueKeys.add(key);
        payload.push(conv);
      } else {
        const fromUomName =
          uoms.find((u) => u.id === conv.fromUomId)?.code || "unit";
        const toUomName =
          uoms.find((u) => u.id === conv.toUomId)?.code || "unit";
        toast.warn(
          `Duplicate conversion rule found for ${fromUomName} to ${toUomName}. Only the first one will be saved.`
        );
      }
    }

    if (payload.length === 0) {
      toast.warn(
        "Please enter at least one complete and valid conversion rule."
      );
      setIsLoading(false);
      return;
    }

    const dataToAccounting = []

    try {
      const apiUrl = `${domain}/api/UomConversions`;

      if(!conversionToEdit){
        await axios.post(apiUrl, payload);

        const _itemCode = await getItemCode(payload?.productId);
        
        const _data = {
          itemCode: _itemCode ?? "",
          fromUomName: payload?.fromUomCode ?? "",
          toUomName: payload?.toUomCode ?? "",
          conversionRate: payload?.itemCode ?? 0,
        }

        dataToAccounting.push(_data)
      } else {
        await axios.put(`${apiUrl}/${conversionToEdit?.id}`, payload[0]);

        const _itemCode = await getItemCode(payload[0]?.productId);
        
        const _data = {
          itemCode: _itemCode ?? "",
          fromUomName: payload[0]?.fromUomCode ?? "",
          toUomName: payload[0]?.toUomCode ?? "",
          conversionRate: payload[0]?.itemCode ?? 0,
        }

        dataToAccounting.push(_data)
      }
      toast.success(`${payload.length} UOM Conversion(s) added successfully`);
      if (refreshData) refreshData();
      onClose();
    } catch (error) {
      console.error(
        "Error submitting form:",
        error.response?.data || error.message
      );
      toast.error(
        `Error adding conversions: ${
          error.response?.data?.title || error.message
        }`
      );
    } finally {
      if(dataToAccounting.length > 0){
        await handleSyncToAccounting(dataToAccounting)
      }
      setIsLoading(false);
    }
  };

  const handleSyncToAccounting = async (_data) => {
    try {
      await axios.post(`${domainAccounting}/api/Conversion/bulk`, _data);
      toast.success(
        `Successfully pushed ${_data?.length || 0 } to Accounting!`,
      );

    } catch (err) {
      console.error(err);
      toast.error(`Failed to push to Accounting.`);
    }
  };

  const handleProductChange = async (id) => {
    setProductId(id);
  };

  return (
    <div className="relative w-full max-w-4xl mx-auto p-4 sm:p-6 lg:p-8 ">
      {isLoading && <Loader />}
      <button
        onClick={onClose}
        className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="h-6 w-6"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M6 18L18 6M6 6l12 12"
          />
        </svg>
      </button>

      <div className="mb-6">
        <h2 className="text-3xl font-bold text-gray-900">
          UOM Conversion Rules
        </h2>
        <p className="mt-2 text-md text-gray-600">
          Define conversion rules by adding rows. For example: 1 Box = 12
          Pieces.
        </p>
      </div>

      <form onSubmit={handleFormSubmit}>
        <div className="space-y-4">
          <div className="md:col-span-5 relative">
            <label className="block text-xs font-semibold text-slate-600 mb-1">
              Items <span className="text-red-500">*</span>
            </label>
            <SearchableSelect
              options={itemList}
              value={productId}
              onChange={handleProductChange}
              placeholder="Search / Select Regular Product..."
            />
          </div>

          {/* Header Row */}
          <div className=" hidden md:grid grid-cols-12 gap-4 items-center text-left text-xs font-medium text-gray-500 uppercase tracking-wider px-2">
            <div className="col-span-1"></div>
            <div className="col-span-4">From Unit</div>
            <div className="col-span-1 text-center"></div>{" "}
            {/* For the '=' sign */}
            <div className="col-span-2">Rate</div>
            <div className="col-span-4">To Unit</div>
          </div>

          {/* Dynamic Conversion Rows */}
          {conversions.map((conv, index) => {
            return (
              <div
                key={conv.id}
                className="relative grid grid-cols-12 gap-x-4 gap-y-2 items-center p-2 rounded-lg transition-colors hover:bg-gray-50"
              >
                {/* Row number / Label */}
                <div className="col-span-12 md:col-span-1 flex items-center justify-end">
                  <span className="font-semibold text-gray-700">1</span>
                </div>

                {/* From UOM Dropdown */}
                <div className="col-span-12 md:col-span-3">
                  <label className="text-sm font-medium text-gray-700 md:hidden">
                    From Unit
                  </label>
                  <select
                    value={conv.fromUomId}
                    onChange={(e) =>
                      handleConversionChange(conv.id, "fromUomId", e.target.value)
                    }
                    className="mt-1 block w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-orange-500 focus:border-orange-500"
                  >
                    <option value="" disabled>
                      Select Unit
                    </option>
                    {uoms.map((uom) => (
                      <option key={uom.id} value={uom.id}>
                        {uom.code}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Equals Sign */}
                <div className="hidden md:flex col-span-1 items-center justify-center">
                  <span className="text-gray-600 font-bold text-lg">=</span>
                </div>

                {/* Conversion Rate Input */}
                <div className="col-span-6 md:col-span-3">
                  <label className="text-sm font-medium text-gray-700 md:hidden">
                    Rate
                  </label>
                  <input
                    type="number"
                    step="any"
                    placeholder="e.g., 12"
                    value={conv.conversionRate}
                    onChange={(e) =>
                      handleConversionChange(
                        conv.id,
                        "conversionRate",
                        e.target.value
                      )
                    }
                    className="mt-1 block w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-orange-500 focus:border-orange-500"
                  />
                </div>

                {/* To UOM Dropdown */}
                <div className="col-span-6 md:col-span-3">
                  <label className="text-sm font-medium text-gray-700 md:hidden">
                    To Unit
                  </label>
                  <select
                    value={conv.toUomId}
                    onChange={(e) =>
                      handleConversionChange(conv.id, "toUomId", e.target.value)
                    }
                    className="mt-1 block w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-orange-500 focus:border-orange-500"
                  >
                    <option value="" disabled>
                      Select Unit
                    </option>
                    {uoms.map((uom) => (
                      <option key={uom.id} value={uom.id}>
                        {uom.code}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Remove Button */}
                <div className="col-span-12 md:col-span-1 flex items-center justify-end">
                  <button
                    type="button"
                    onClick={() => handleRemoveConversionRow(conv.id)}
                    // className="absolute -top-3 -right-2 text-red-500 hover:text-red-700 p-2 rounded-full hover:bg-red-100"
                    className="hidden -top-3 -right-2 text-red-500 hover:text-red-700 p-2 rounded-full hover:bg-red-100"
                    title="Remove this rule"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-5 w-5"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </button>
                </div>
              </div>
            )
          })}

          {/* Fallback for when no rows exist */}
          {conversions.length === 0 && !isLoading && (
            <div className="text-center py-8 text-gray-500 border-2 border-dashed rounded-lg">
              <p>No conversion rules have been added yet.</p>
              <p>Click the button below to add your first rule.</p>
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="mt-8 flex flex-col-reverse sm:flex-row justify-end items-center gap-4">
          <button
            type="button"
            onClick={handleAddConversionRow}
            className="hidden w-full sm:w-auto flex items-center justify-center gap-2 bg-gray-100 py-3 px-6 text-center text-gray-700 font-semibold rounded-md border border-gray-300 hover:bg-gray-200 transition duration-200"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-11a1 1 0 10-2 0v2H7a1 1 0 100 2h2v2a1 1 0 102 0v-2h2a1 1 0 100-2h-2V7z"
                clipRule="evenodd"
              />
            </svg>
            Add Conversion Rule
          </button>
          <button
            type="submit"
            className="w-full sm:w-auto bg-orange-600 py-3 px-8 text-center text-white font-semibold rounded-md hover:bg-orange-700 transition duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500"
          >
            Save
          </button>
        </div>
      </form>
    </div>
  );
};

export default AddUomConversion;
